import * as React from 'react'

import { useFormContext, type RegisterOptions } from 'react-hook-form'

import { cn } from '@/lib/utils'

import { controlVariants, type ControlSize } from './control-styles'
import { useMergedRef } from './lib/merge-refs'
import { useControlName } from './lib/use-control-name'
import { useLegacyFieldAria } from './lib/use-legacy-field-aria'

/**
 * Campo de texto da camada canônica.
 *
 * Registra-se sozinho no `react-hook-form` a partir do `name` publicado pelo
 * `Field` — quem consome não conecta valor nem erro na mão.
 *
 * O `ref` precisa ser mesclado: um vem do `register`, outro do `Field.Control`,
 * que é um `Slot` e injeta `id` e os atributos ARIA no filho. Ignorar qualquer
 * um dos dois quebra ou o formulário ou a acessibilidade.
 */

type TextFieldProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'size' | 'name'> & {
  size?: ControlSize
  /** Só é necessário fora de `<Field>` — ver `useControlName`. */
  name?: string
  /** Regras do react-hook-form. Prefira validar por schema; isto existe para
   *  paridade com os campos legados durante a migração. */
  rules?: RegisterOptions
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ className, size, type = 'text', name: nameProp, rules, onChange, onBlur, ...props }, ref) => {
    const name = useControlName(nameProp)
    const legacyAria = useLegacyFieldAria(name)
    const { register } = useFormContext()
    const { ref: registerRef, onChange: registerChange, onBlur: registerBlur, ...field } =
      register(name, rules)

    // O handler do consumidor é composto com o do register, nunca o substitui:
    // espalhar `props` por cima de `field` faria um `onChange` de fora derrubar
    // o registro do formulário, e o campo pararia de gravar valor em silêncio.
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      void registerChange(event)
      onChange?.(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      void registerBlur(event)
      onBlur?.(event)
    }

    const mergedRef = useMergedRef(registerRef, ref)

    return (
      <input
        type={type}
        className={cn(controlVariants({ size }), className)}
        {...legacyAria}
        {...field}
        {...props}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={mergedRef}
      />
    )
  }
)
TextField.displayName = 'TextField'
